import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {ErrorState} from 'shells/error-state/error-state';
import {AccountIcon} from 'shells/icon/svg/account-icon';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {LeadIcon} from 'shells/icon/svg/lead-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {ListView} from 'shells/list-view/list-view';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {
    GetEmailMarketingSavedFilters_emailMarketingSavedFilters as SavedFilter,
    EditionFragment_audience_fromSavedFilter as EditionSavedFilter,
} from 'nutshell-graphql-types';

import {getHumanReadableEntityTypeFromGraphQLEnum} from '../../../../../core/utils/get-human-readable-entity-type-from-graphql-enum';

import {useGetEmailMarketingSavedFilters} from '../../graphql-wrappers/saved-filters/graphql/hooks/use-get-email-marketing-saved-filters';

import type {ListRecipientSelection as RecipientSelection} from './new-recipients-selection-view';
import {RecipientSelectorListItem} from './recipient-selector-list-item';
import {SavedListCreatedTime} from './saved-list-created-time';

import styles from './saved-list-selector.module.css';

type Props = {
    setSelectedRecipients: (arg1?: RecipientSelection | null | undefined) => void;
    selectedRecipients: RecipientSelection | null | undefined;
};

export function SavedListSelector(props: Props) {
    const {
        emailMarketingSavedFilters: savedLists,
        error,
        isLoading,
    } = useGetEmailMarketingSavedFilters('NAME_ASC');

    React.useEffect(() => {
        if (!savedLists.length && !isLoading && props.selectedRecipients) {
            props.setSelectedRecipients(null);
        }

        if (savedLists.length > 0 && !props.selectedRecipients) {
            const firstList = savedLists[0];
            if (!firstList) {
                return;
            }
            const count = firstList.count.value;
            props.setSelectedRecipients({
                id: firstList.id,
                name: firstList.name,
                htmlUrlPath: firstList.htmlUrlPath,
                entityType: firstList.entityType,
                filterString: firstList.filterString,
                recipientCount: count,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedLists]);

    return (
        <ListView
            specialStates={[
                {
                    shouldRender: () => isLoading,
                    component: <LoadingPage />,
                    centerVertically: true,
                },
                {
                    shouldRender: () => Boolean(error),
                    component: <ErrorState errorMessage={error} />,
                },
                {
                    shouldRender: () => !isLoading && !savedLists.length,
                    component: (
                        <EmptyStateLarge
                            title='Your team doesn’t have any saved lists yet'
                            subtitle={
                                <p>
                                    {' '}
                                    {/* @ts-expect-error */}
                                    <Link variant='primary' size='big' as={RoutedLink} to='/people'>
                                        Create one
                                    </Link>{' '}
                                    by saving filters on your Person, Company, or Lead lists
                                </p>
                            }
                            icon={
                                <div style={{width: 120, height: 120}}>
                                    <Illustration
                                        src='cactus-empty-state'
                                        width='100%'
                                        height='100%'
                                    />
                                </div>
                            }
                        />
                    ),
                    centerVertically: true,
                },
            ]}
            isLoading={isLoading}
            renderRow={(savedList: SavedFilter) => {
                return (
                    <RecipientSelectorListItem
                        key={savedList.id}
                        title={savedList.name}
                        subtitle={getSubscribersElement(savedList)}
                        isSelected={Boolean(
                            props.selectedRecipients && props.selectedRecipients.id === savedList.id
                        )}
                        onClick={() => {
                            const count = savedList.count.value;
                            props.setSelectedRecipients({
                                id: savedList.id,
                                name: savedList.name,
                                htmlUrlPath: savedList.htmlUrlPath,
                                entityType: savedList.entityType,
                                filterString: savedList.filterString,
                                recipientCount: count,
                            });
                        }}
                        actionElement={
                            savedList.createdTime ? (
                                <div className='flex align-center'>
                                    <SavedListCreatedTime createdTime={savedList.createdTime} />
                                </div>
                            ) : undefined
                        }
                    />
                );
            }}
            collection={savedLists}
            itemSpacing={8}
            listPadding={16}
        />
    );
}

export function getSubscribersElement(
    savedList: SavedFilter | EditionSavedFilter
): React.ReactElement<any> {
    const count = savedList.count.value;
    const type = savedList.entityType;

    let icon = <AccountIcon size={16} />;
    if (type === 'CONTACT') {
        icon = <ContactIcon size={16} />;
    }
    if (type === 'LEAD') {
        icon = <LeadIcon size={14} />;
    }

    const humanReadableType = getHumanReadableEntityTypeFromGraphQLEnum(
        savedList.entityType,
        count !== 1
    );

    return (
        <div className={styles.count}>
            <div className={styles.icon}>{icon}</div>
            <div>
                {count.toLocaleString()}&nbsp;{humanReadableType}
            </div>
        </div>
    );
}
